/**
 * Spec 005 — meetings & voting fetch wrappers. Same discriminated-union contract as lib/auth.ts /
 * lib/members.ts: these NEVER throw; each returns an explicit outcome the UI renders across the four
 * states. All requests send cookies; mutations echo the double-submit CSRF token. A `conflict` result
 * carries the server's specific message (the §12/§13/quorum/already-voted messages) so the UI can show
 * it verbatim — never a silent or generic error.
 */
import { readCsrfToken } from './auth';

const TIMEOUT_MS = 10_000;

export type Result<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'invalid'; errors: Record<string, string> }
  | { kind: 'conflict'; message: string }
  | { kind: 'forbidden'; message: string }
  | { kind: 'notfound' }
  | { kind: 'error' };

// ---- shapes ----
export type MeetingType = 'annual' | 'extra';
export type MeetingStatus = 'draft' | 'published' | 'in_progress' | 'concluded' | 'cancelled';
export type BallotKind = 'simple' | 'qualified' | 'election';
export type BallotState = 'pending' | 'open' | 'closed';
export type VoteChoice = 'yes' | 'no' | 'abstain';

export interface MeetingRow {
  id: string;
  type: MeetingType;
  title: string;
  scheduledAt: string;
  location: string | null;
  status: MeetingStatus;
  motionDeadline: string;
  noticePublishedAt: string | null;
}

export interface AgendaItem {
  id: string;
  position: number;
  title: string;
  kind: string;
  motionId: string | null;
  secrecyRequested: boolean;
  // Spec 005c (F8): the item's current ballot, embedded by GET /api/meetings/:id for participants
  // (board or registered attendees) so a member who reloads a live meeting can see and vote on it.
  // Absent/null when the caller isn't a participant or no ballot has been opened.
  ballot?: BallotView | null;
}

export interface MeetingDetail extends MeetingRow {
  quorumThreshold: number | null;
  chairMemberId: string | null;
  agendaItems: AgendaItem[];
}

export interface MotionRow {
  id: string;
  title: string;
  body: string;
  status: 'submitted' | 'on_agenda' | 'withdrawn' | 'noted' | 'rejected';
  submitterAccountId: string | null;
  createdAt: string;
}

export interface Tally {
  yes: number;
  no: number;
  abstain: number;
}
export interface BallotView {
  id: string;
  agendaItemId: string;
  kind: BallotKind;
  secrecy: 'open' | 'secret';
  state: BallotState;
  result: string | null;
  tally: Tally;
  entries?: Array<{ choice: string; voterAccountId: string | null; onBehalfOfAccountId: string | null }>;
}

async function req(method: string, path: string, body?: unknown): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }
  try {
    return await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

/** Map a Response to the discriminated union, pulling the server's specific message on 4xx. */
async function toResult<T>(res: Response): Promise<Result<T>> {
  if (res.ok) return { kind: 'ok', data: (await res.json().catch(() => ({}))) as T };
  const body = (await res.json().catch(() => ({}))) as { message?: string; errors?: Record<string, string> };
  if (res.status === 400) return { kind: 'invalid', errors: body.errors ?? {} };
  if (res.status === 403) return { kind: 'forbidden', message: body.message ?? 'You don’t have permission to do that.' };
  if (res.status === 404) return { kind: 'notfound' };
  if (res.status === 409) return { kind: 'conflict', message: body.message ?? 'That action isn’t possible right now.' };
  return { kind: 'error' };
}

const call = async <T>(method: string, path: string, body?: unknown): Promise<Result<T>> => {
  try {
    return await toResult<T>(await req(method, path, body));
  } catch {
    return { kind: 'error' };
  }
};

// ---- meetings ----
export const listMeetings = () => call<MeetingRow[]>('GET', '/api/meetings');
export const getMeeting = (id: string) => call<MeetingDetail>('GET', `/api/meetings/${id}`);
export const createMeeting = (b: { type: MeetingType; title: string; scheduledAt: string; location?: string; quorumThreshold?: number }) =>
  call<MeetingDetail>('POST', '/api/meetings', b);
export const setAgenda = (id: string, items: Array<{ position: number; title: string; kind?: string }>) =>
  call<AgendaItem[]>('POST', `/api/meetings/${id}/agenda`, { items });
export const publishMeeting = (id: string) => call<MeetingRow>('POST', `/api/meetings/${id}/publish`);
export const startMeeting = (id: string) => call<MeetingRow>('POST', `/api/meetings/${id}/start`);
export const concludeMeeting = (id: string) => call<MeetingRow>('POST', `/api/meetings/${id}/conclude`);
export const cancelMeeting = (id: string, reason?: string) => call<MeetingRow>('POST', `/api/meetings/${id}/cancel`, { reason });

// ---- motions ----
export const listMotions = (id: string) => call<MotionRow[]>('GET', `/api/meetings/${id}/motions`);
export const submitMotion = (id: string, b: { title: string; body: string }) => call<MotionRow>('POST', `/api/meetings/${id}/motions`, b);
export const withdrawMotion = (id: string) => call<MotionRow>('POST', `/api/motions/${id}/withdraw`);
export const placeMotionOnAgenda = (id: string) => call<MotionRow>('POST', `/api/motions/${id}/place-on-agenda`, {});

// ---- attendance + proxy ----
export type ProxyStatus = 'active' | 'revoked' | 'voided' | 'exercised';
export interface ProxyRow { id: string; grantorAccountId: string; holderAccountId: string; status: ProxyStatus }
export interface ProxyCandidate { accountId: string; displayName: string }
/** Spec 005g — viewer-scoped proxy state for the MeetingDetail proxy panel. `myDelegation` is the
 *  viewer's own active delegation (as grantor); `proxiesHeld` are proxies they carry (as holder). */
export interface ProxyContext {
  viewerPresent: boolean;
  delegable: boolean;
  candidates: ProxyCandidate[];
  myDelegation: { proxyId: string; holderAccountId: string; holderName: string } | null;
  proxiesHeld: Array<{ proxyId: string; grantorAccountId: string; grantorName: string }>;
}

export const registerAttendance = (id: string) => call<{ present: boolean }>('POST', `/api/meetings/${id}/attendance`);
export const getRoster = (id: string) => call<Array<{ memberAccountId: string; registeredAt: string }>>('GET', `/api/meetings/${id}/attendance`);
export const grantProxy = (id: string, holderMemberId: string) => call<{ id: string }>('POST', `/api/meetings/${id}/proxies`, { holderMemberId });
export const revokeProxy = (proxyId: string) => call<{ id: string }>('POST', `/api/proxies/${proxyId}/revoke`);
export const getProxies = (id: string) => call<ProxyRow[]>('GET', `/api/meetings/${id}/proxies`);
export const getProxyContext = (id: string) => call<ProxyContext>('GET', `/api/meetings/${id}/proxy-context`);

// ---- ballots + voting ----
export const requestSecrecy = (agendaItemId: string) => call<{ secrecyRequested: boolean }>('POST', `/api/agenda-items/${agendaItemId}/secrecy-request`);
export const openBallot = (agendaItemId: string, b: { kind: BallotKind; secrecy: 'open' | 'secret' }) =>
  call<BallotView>('POST', `/api/agenda-items/${agendaItemId}/ballots`, b);
export const getBallot = (id: string) => call<BallotView>('GET', `/api/ballots/${id}`);
export const castVote = (id: string, choice: VoteChoice, onBehalfOf?: string) =>
  call<{ recorded: boolean }>('POST', `/api/ballots/${id}/vote`, { choice, onBehalfOf });
export const closeBallot = (id: string, chairCastingDirection?: 'yes' | 'no') =>
  call<BallotView>('POST', `/api/ballots/${id}/close`, { chairCastingDirection });

// ---- minutes (spec 005/005h) ----
export interface MinutesAddendum { id: string; body: string; createdAt: string }
export interface MinutesView {
  id: string;
  meetingId: string;
  status: 'draft' | 'published';
  body: string;
  publishedAt: string | null;
  addenda: MinutesAddendum[];
}
/** Spec 005h — discover a meeting's minutes on load (role-gated): the full record, or `null` when
 *  nothing is visible (no minutes yet, or a draft the viewer isn't board and may not see, SC-536).
 *  The server wraps the result in `{ minutes }` so a null is an unambiguous JSON body; we unwrap it. */
export const getMeetingMinutes = async (meetingId: string): Promise<Result<MinutesView | null>> => {
  const r = await call<{ minutes: MinutesView | null }>('GET', `/api/meetings/${meetingId}/minutes`);
  return r.kind === 'ok' ? { kind: 'ok', data: r.data.minutes ?? null } : r;
};
export const compileMinutes = (meetingId: string) => call<MinutesView>('POST', `/api/meetings/${meetingId}/minutes`);
export const getMinutes = (id: string) => call<MinutesView>('GET', `/api/minutes/${id}`);
export const editMinutes = (id: string, body: string) => call<MinutesView>('PATCH', `/api/minutes/${id}`, { body });
export const publishMinutes = (id: string) => call<MinutesView>('POST', `/api/minutes/${id}/publish`);
export const addAddendum = (id: string, body: string) => call<MinutesAddendum>('POST', `/api/minutes/${id}/addenda`, { body });
